import { Alert, CircularProgress, Divider, Grid, Icon, Link } from "@mui/material";
import PropTypes from "prop-types";
import dayjs from "dayjs";
import { Link as RouterLink } from "react-router-dom";
import SoftBadge from "components/SoftBadge";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import { getUproColor } from "utils/colors";
import { nombreComandera } from "./PasoCobro";
import {
  PASOS, describirVoucher, duracionTexto, formatoFecha, formatoPesos, productosHabilitados, resumenBloqueo, tieneCatalogo, mensajeErrorEvento,
} from "../../utils";

// Las pantallas relacionadas se abren aparte para no perder lo cargado en el asistente.
const enPestanaNueva = { target: "_blank", rel: "noopener noreferrer" };

// Sin irAPaso (modo lectura) no se ofrece "Cambiar".
const Seccion = ({ titulo, paso, irAPaso, children }) => (
  <SoftBox py={1.5}>
    <SoftBox display="flex" justifyContent="space-between" alignItems="center" gap={1} mb={0.5}>
      <SoftTypography variant="button" fontWeight="bold">{titulo}</SoftTypography>
      {irAPaso && <SoftTypography component="button" type="button" variant="caption" fontWeight="bold" aria-label={`Cambiar ${titulo}`} onClick={() => irAPaso(paso)}
        sx={{ border: 0, background: "transparent", padding: 0, cursor: "pointer", color: getUproColor("uproGreen"), display: "flex", alignItems: "center", gap: 0.5 }}>
        <Icon fontSize="small">edit</Icon>Cambiar
      </SoftTypography>}
    </SoftBox>
    <SoftTypography variant="caption" color="text" component="div">{children}</SoftTypography>
  </SoftBox>
);

Seccion.propTypes = {
  titulo: PropTypes.string.isRequired, paso: PropTypes.number.isRequired,
  irAPaso: PropTypes.func, children: PropTypes.node.isRequired,
};

Seccion.defaultProps = { irAPaso: null };

const fechaCorta = (dia) => dayjs(dia).format("DD/MM");
const bordeSx = { border: ({ borders: { borderWidth, borderColor } }) => `${borderWidth[1]} solid ${borderColor}` };

export default function PasoRevision({ form, opciones, irAPaso, validar, diagnostico, soloLectura }) {
  const cambiar = soloLectura ? null : irAPaso;
  const habilitados = productosHabilitados(form);
  const medios = (opciones?.mediosPago || []).filter((medio) => !form.mediosPago || form.mediosPago.includes(medio.codigo));
  const comandera = (opciones?.comanderas || []).find((item) => String(item.id) === String(form.idComandera || opciones?.idComanderaPredeterminada));
  const { data, loading, error } = diagnostico;
  const vouchers = data?.vouchersFisicos || [];

  const avisos = [];
  // Lo que impide guardar se muestra acá, con el paso donde corregirlo.
  PASOS.slice(0, -1).forEach((paso, index) => {
    Object.values(validar(index)).forEach((texto) => {
      avisos.push({
        severidad: "error",
        texto: <>{texto} <Link component="button" type="button" onClick={() => irAPaso(index)}>Ir a {paso.titulo}</Link></>,
      });
    });
  });
  if (data?.truncado) {
    avisos.push({ severidad: "warning", texto: `Los controles revisan solo los primeros ${data.dias.length} días del evento.` });
  }
  if (data?.eventosSolapados?.length) {
    avisos.push({
      severidad: "error",
      texto: `Se superpone con ${data.eventosSolapados.map((evento) => `«${evento.nombre}» (${formatoFecha(evento.inicioArgentina)} a ${formatoFecha(evento.finArgentina)})`).join(", ")}. Cambie el horario o desactive uno de los dos.`,
    });
  }
  if (!tieneCatalogo(form)) avisos.push({ severidad: "warning", texto: "No hay productos a la venta ni menú universitario." });
  if (form.menuUniversitario && data && !data.categoriaMenuEncontrada) {
    avisos.push({ severidad: "warning", texto: "No existe la categoría «Menú universitario»: no se mostrarán platos." });
  } else if (form.menuUniversitario && data?.turnosActivos === 0) {
    avisos.push({ severidad: "warning", texto: "No hay turnos activos en Villa Mercedes: no se mostrarán platos." });
  }
  if (form.menuUniversitario && data?.diasSinMenu?.length) {
    avisos.push({
      severidad: "warning",
      texto: <>Sin platos en el cronograma para {data.diasSinMenu.map(fechaCorta).join(", ")}. Cárguelos en <Link component={RouterLink} to="/menuUniversitario" {...enPestanaNueva}>Menú universitario</Link>.</>,
    });
  }

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        {loading && (
          <SoftBox display="flex" alignItems="center" gap={1}>
            <CircularProgress size={16} /><SoftTypography variant="caption">Verificando configuración…</SoftTypography>
          </SoftBox>
        )}
        {error && <Alert severity="warning">No se pudieron hacer los controles automáticos ({mensajeErrorEvento(error)}). Puede guardar igual.</Alert>}
        <SoftBox display="grid" gap={1}>
          {avisos.map((aviso, index) => <Alert key={index} severity={aviso.severidad}>{aviso.texto}</Alert>)}
        </SoftBox>
      </Grid>

      <Grid item xs={12} md={7}>
        <SoftBox px={2} borderRadius="lg" sx={bordeSx}>
          <Seccion titulo="Datos del evento" paso={0} irAPaso={cambiar}>
            <strong>{form.nombre}</strong> · {formatoFecha(form.inicio)} a {formatoFecha(form.fin)} ({duracionTexto(form.inicio, form.fin)})
            &nbsp;<SoftBadge color={form.activo ? "uproGreen" : "secondary"} badgeContent={form.activo ? "Activo" : "Desactivado"} size="xs" container />
          </Seccion>
          <Divider />
          <Seccion titulo="Productos a la venta" paso={1} irAPaso={cambiar}>
            {habilitados.length ? habilitados.map((item) => (
              `${item.producto?.nombre || `#${item.idProducto}`} ${item.precioEvento === "" ? "(carta)" : formatoPesos(item.precioEvento)}`
            )).join(" · ") : "Ninguno"}
            {form.productos.length > habilitados.length && ` · ${form.productos.length - habilitados.length} pausado(s)`}
          </Seccion>
          <Divider />
          <Seccion titulo="Menú universitario" paso={2} irAPaso={cambiar}>
            {!form.menuUniversitario ? "No se venden platos"
              : form.menuModoPrecio === "FIJO" ? `Platos del cronograma a ${formatoPesos(form.menuUniversitarioPrecio)}`
                : "Platos del cronograma con la tarifa de cada comprador"}
          </Seccion>
          <Divider />
          <Seccion titulo="Cobro e impresión" paso={3} irAPaso={cambiar}>
            {medios.map((medio) => medio.etiqueta).join(", ") || "—"} · Comandas en {comandera ? nombreComandera(comandera) : `#${form.idComandera || opciones?.idComanderaPredeterminada || "—"}`}
            {!form.idComandera && " (predeterminada)"}
          </Seccion>
          <Divider />
          <Seccion titulo="App de autogestión" paso={4} irAPaso={cambiar}>
            {resumenBloqueo(form)}
            {(form.bloqueaAppCompleta || form.bloqueaMenu || form.bloqueaCafeteria) && form.mensajeBloqueo.trim() && <> · «{form.mensajeBloqueo.trim()}»</>}
          </Seccion>
        </SoftBox>
      </Grid>

      <Grid item xs={12} md={5}>
        <SoftBox p={2} borderRadius="lg" sx={bordeSx}>
          <SoftTypography variant="button" fontWeight="bold" display="block">Vouchers físicos en estas fechas</SoftTypography>
          <SoftTypography variant="caption" color="secondary" display="block" mb={1}>
            Se administran en <Link component={RouterLink} to="/beneficios-vouchers" {...enPestanaNueva}>Beneficios y vouchers</Link>.
          </SoftTypography>
          {soloLectura && <SoftTypography variant="caption">No se revisan en un evento finalizado.</SoftTypography>}
          {data && !vouchers.length && <SoftTypography variant="caption">Ninguno.</SoftTypography>}
          <SoftBox display="grid" gap={1}>
            {vouchers.map((voucher) => (
              <SoftBox key={voucher.idVoucher} display="flex" gap={1} alignItems="flex-start">
                <Icon fontSize="small" sx={{ color: getUproColor(voucher.problema ? "uproYellow" : "uproGreen"), mt: 0.25 }}>
                  {voucher.problema ? "warning" : "check_circle"}
                </Icon>
                <SoftBox>
                  <SoftTypography variant="caption" fontWeight="bold" display="block">
                    {voucher.grupo} · {describirVoucher(voucher)}
                  </SoftTypography>
                  <SoftTypography variant="caption" color="secondary" display="block">
                    {dayjs(voucher.desde).format("DD/MM")} al {dayjs(voucher.hasta).format("DD/MM")}
                    {voucher.problema && ` · ${voucher.problema}`}
                  </SoftTypography>
                </SoftBox>
              </SoftBox>
            ))}
          </SoftBox>
        </SoftBox>
      </Grid>
    </Grid>
  );
}

PasoRevision.propTypes = {
  form: PropTypes.object.isRequired,
  opciones: PropTypes.object,
  irAPaso: PropTypes.func.isRequired,
  validar: PropTypes.func,
  soloLectura: PropTypes.bool,
  diagnostico: PropTypes.shape({ data: PropTypes.object, loading: PropTypes.bool, error: PropTypes.object }).isRequired,
};

PasoRevision.defaultProps = { validar: () => ({}), soloLectura: false };
